/* eslint-disable max-lines */
import React, { FC, ReactNode, useState, useEffect } from 'react';
import styled from 'styled-components';

import ApiDetector from '^/components/atoms/ApiDetector';

import withL10n, { L10nProps } from '^/components/atoms/WithL10n';

import MemberBoard from '^/components/organisms/MemberBoard';
import * as T from '^/types';
import { isRoleAdmin } from '^/utilities/role-permission-check';
import { isUserAllowToCreateAndDeleteProject } from '^/utilities/user-role-check';
import { getUserInfo } from '^/store/react-query/users';
import { useDispatch, useSelector } from 'react-redux';
import { useGetProjectDetail } from '^/hooks/projects/useGetProjectDetail';
import { useParams } from 'react-router-dom';
import { usePopupStore } from '^/store/popup';
import { OpenProjectPagePopup } from '^/store/duck/Pages';

const Root = styled.div({
  boxSizing: 'border-box',
  width: '100%',
  paddingLeft: '60px',
  paddingRight: '30px',
});

const isAdmin: (project?: T.Project) => boolean = project =>
  project !== undefined && isRoleAdmin(project.permissionRole);

export interface Props {
  readonly timezoneOffset: number;
  /**
   * @todo Remove the case of undefined project
   */
  readonly project?: T.Project;
  readonly patchStatus: T.APIStatus;
  readonly auth: T.AuthState;
  readonly slug: T.PlanConfig['slug'];
  onDeleteClick(): void;
  onShareClick(): void;
  onSubmit(
    id: number,
    title: string,
    description: string,
    coordinate?: T.ProjectionEnum,
    logo?: File,
    unit?: T.ValidUnitType,
    projectConfig?: T.Project['projectConfig']
  ): void;
  changeAuthedUser(): void;
  updateCoordinates(
    contents: T.Content,
    from: T.ProjectionEnum,
    to: T.ProjectionEnum
  ): Promise<void>;
  displayNoPermissionPopup(): void;
}

export interface State {
  readonly projectId?: T.ProjectWithConfig['id'];
  readonly title: string;
  readonly description: string;
  readonly coordinateSystem?: T.ProjectionEnum;
  readonly unit?: T.Project['unit'];
  readonly logo?: {
    readonly raw: File;
    readonly url: string;
  };
  isEditMode: boolean;
  readonly projectConfig?: T.Project['projectConfig'];
}

const ProjectManageTab: FC<Props & L10nProps> = () => {
  const params = useParams();
  const projectId = params.id;

  const dispatch = useDispatch();

  const setPopupOptions = usePopupStore(s => s.setPopupOptions);
  const setMinorPopup = usePopupStore(s => s.setMinorPopup);

  const authedUser = useSelector((state: T.State) => state.Auth.authedUser);
  const { projectById } = useSelector((s: T.State) => ({
    projectById: s.Projects.projects.byId,
  }));
  const project = projectById[Number(projectId)];
  const hasProject = Boolean(project);
  useGetProjectDetail({ projectId, hasProject });

  const user = getUserInfo(authedUser!.id);
  const isOwner: boolean = project?.owner.id === user.id;
  const isUserAllowedToCreateAndDeleteProject = user?.role
    ? isUserAllowToCreateAndDeleteProject(user.role)
    : false;

  const [projectState, setProjectState] = useState<State>(
    project !== undefined
      ? {
          projectId: project.id,
          title: project.title,
          description: project.description,
          coordinateSystem:
            project.coordinateSystem !== undefined ? project.coordinateSystem : undefined,
          unit: project.unit,
          isEditMode: false,
          projectConfig: project.projectConfig,
        }
      : {
          title: '',
          description: '',
          isEditMode: false,
          projectConfig: {
            withGcp: false,
            withRtk: false,
          },
        }
  );

  useEffect(() => {
    if (project === undefined) {
      return;
    }
    setProjectState({
      projectId: project.id,
      title: project.title,
      description: project.description,
      coordinateSystem:
        project.coordinateSystem !== undefined ? project.coordinateSystem : undefined,
      unit: project.unit,
      isEditMode: false,
      projectConfig: project.projectConfig,
    });
  }, [project]);

  const handleShareClick: () => void = () => {
    setPopupOptions({ manageProjectPopupType: T.ManageProjectPopupType.ADD_COLLABORATOR });
    setMinorPopup(T.ContentPageMinorPopupType.MANAGE_PROJECT_POPUP);
  };

  const handlePatchSuccess: () => void = () => {
    changeMode();
  };

  const handlePatchError: () => void = () => {
    changeMode();
  };

  const changeMode: () => void = () => {
    setProjectState({ ...projectState, isEditMode: !projectState.isEditMode });
  };

  if (project === undefined) {
    return null;
  }

  const handleDeleteClick: () => void = () => {
    dispatch(OpenProjectPagePopup({ popup: T.ProjectPagePopupType.DELETE }));
  };

  const memberBoard: ReactNode = isAdmin(project) ? (
    <MemberBoard
      handleShareClick={handleShareClick}
      handleDeleteClick={handleDeleteClick}
      isUserAllowToCreateAndDeleteProject={isUserAllowedToCreateAndDeleteProject && isOwner}
    />
  ) : undefined;

  return (
    <Root>
      <ApiDetector
        status={T.APIStatus.SUCCESS}
        onSuccess={handlePatchSuccess}
        onError={handlePatchError}
      />
      {memberBoard}
    </Root>
  );
};
export default withL10n(ProjectManageTab);
